import {
  IconQrcode,
  IconHandFinger,
  IconTextSize,
  IconUsb,
} from '@tabler/icons-react'
import { useCallback, useRef, useState } from 'react'
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from 'ui'
import { DrawSignature } from './Draw'
import { SignatureHandler } from './Pad'
import { SignatureQRCode } from './SignatureSession'
import { WriteSignature } from './Write'
import { signatureFonts } from './fonts'
import { useSignature } from './Context'
import { useDeviceDetect } from '@autosign/core/hooks/useDeviceDetect'
import { useSignatureDimensions } from './SignatureDimensionsContext'
import { Recipient } from 'database'
import { TokenSignature } from './TokenSignature'
import { useSigning } from '../../../../../components/Signing/Context'

export const SignatureComposition = ({
  onClose,
  commitSignature,
  isLoading,
  signer,
}: {
  onClose: () => void
  commitSignature: (signature: string) => void
  isLoading: boolean
  signer: Pick<Recipient, 'name' | 'email'>
}) => {
  const signatureHandlerRef = useRef<SignatureHandler>(null)

  type Tab = 'draw' | 'type' | 'qr' | 'token'

  const [tab, setTab] = useState<Tab>('draw')

  const {
    createSession,
    expireSignatureSession,
    onSignatureReceived,
    retrieveSignatureSession,
  } = useSignature()

  const signing = useSigning()

  const handleConfirm = useCallback(() => {
    if (!signatureHandlerRef.current) return

    const signature = signatureHandlerRef.current.getImage()

    commitSignature(signature)

    onClose()
  }, [])

  const device = useDeviceDetect()
  const { width } = useSignatureDimensions()

  const sideHandleAnchor = useCallback((e: any) => {
    // TODO: Fix anchor click
    e.preventDefault()
    window.open(e.target.href, '_blank')
  }, [])

  return (
    <div className='w-100'>
      {signatureFonts.map((font, index) => {
        return (
          <p
            key={index}
            style={{
              position: 'absolute',
              left: -9999,
            }}
            className={font.className}
          >
            preload
          </p>
        )
      })}

      {device === 'desktop' ? (
        <Tabs
          variant={'underline'}
          onValueChange={setTab as any}
          value={tab}
          className='relative'
        >
          <TabsList className='z-10 flex items-center justify-between px-0'>
            <div className='flex flex-col justify-between items-start text-left'>
              <h2 className='font-semibold text-xl text-[#0F1119]'>
                Firma como {signer.name}
              </h2>
              <p className='text-primary font-light'>{signer.email}</p>
            </div>
            <div className='flex items-center justify-end gap-5 w-1/5'>
              {signing?.hsm_enabled && (
                <TabsTrigger
                  value={'token' satisfies Tab}
                  className='!bg-transparent'
                >
                  <IconUsb />
                </TabsTrigger>
              )}

              <TabsTrigger
                value={'qr' satisfies Tab}
                className='!bg-transparent'
              >
                <IconQrcode />
              </TabsTrigger>
              <TabsTrigger
                value={'type' satisfies Tab}
                className='!bg-transparent'
              >
                <IconTextSize />
              </TabsTrigger>
            </div>
          </TabsList>
          <TabsContent value={'qr' satisfies Tab}>
            <SignatureQRCode
              expireSignatureSession={expireSignatureSession}
              retrieveSignatureSession={retrieveSignatureSession}
              onSignatureReceived={onSignatureReceived}
              createSession={createSession}
            />
          </TabsContent>
          <TabsContent value={'type' satisfies Tab}>
            <WriteSignature
              ref={signatureHandlerRef}
              width={width}
              height={192}
            />
          </TabsContent>
          {signing?.hsm_enabled && (
            <TabsContent value={'token' satisfies Tab}>
              <TokenSignature
                expireSignatureSession={expireSignatureSession}
                retrieveSignatureSession={retrieveSignatureSession}
                onSignatureReceived={onSignatureReceived}
                createSession={createSession}
              />
            </TabsContent>
          )}
        </Tabs>
      ) : (
        <Tabs
          variant={'underline'}
          onValueChange={setTab as any}
          value={tab}
          className='relative'
        >
          <TabsList className='z-10 flex items-center justify-center'>
            <TabsTrigger
              value={'draw' satisfies Tab}
              className='!bg-transparent'
            >
              <IconHandFinger />
            </TabsTrigger>
            <TabsTrigger
              value={'type' satisfies Tab}
              className='!bg-transparent'
            >
              <IconTextSize />
            </TabsTrigger>
          </TabsList>

          <TabsContent value={'draw' satisfies Tab}>
            <DrawSignature className='rounded-none' ref={signatureHandlerRef} />
          </TabsContent>
          <TabsContent value={'type' satisfies Tab}>
            <WriteSignature
              ref={signatureHandlerRef}
              width={width}
              height={192}
            />
          </TabsContent>
        </Tabs>
      )}

      {tab !== 'qr' && tab !== 'token' && (
        <div>
          <p className='text-[8px] md:text-[9.5px] text-muted-foreground max-w-lg w-full mt-4 md:mt-8'>
            Al avanzar con tu firma electrónica, reconoces y consientes que se
            utilizará para firmar el documento indicado y posee la misma validez
            legal que una firma a mano. Al concluir el proceso de firma
            electrónica, confirmas tu entendimiento y aceptación de estas
            condiciones. Consulta la{' '}
            <a
              href={`${process.env.NEXT_PUBLIC_WEB_APP_BASE_URL}/legal/consent`}
              className='underline'
              onClick={sideHandleAnchor}
            >
              divulgación de firma electrónica
            </a>{' '}
            completa.
          </p>
          <div className='w-full flex items-center justify-between mt-6 md:mt-10'>
            <Button
              loading={isLoading}
              onClick={() => onClose()}
              variant={'unstyled'}
              className='w-full flex items-center justify-center'
            >
              Cancelar
            </Button>
            <Button
              loading={isLoading}
              onClick={handleConfirm}
              className='w-full'
            >
              Enviar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
